import type {
  DryBulkNominationRequest,
  DryBulkNominationResponse,
  DryBulkResource,
} from "@services/dry-bulk-nomination/types";
import { convertUaeToUtc } from "@utils/date-time";
import {
  emptyDryBulkNominationForm,
  toNumber,
  type DryBulkNominationFormData,
  type ResourceFormData,
} from "../schema/dryBulkNominationSchema";
import { toUaeWallClockDate } from "./uaeDate";

const blankToUndefined = (value?: string | null) => (value?.trim() ? value.trim() : undefined);
/** Form numbers may still be strings from the input (the drawers store parsed values; the header does not). */
const numberOrUndefined = (value: unknown) => {
  const parsed = toNumber(value);
  return typeof parsed === "number" && !Number.isNaN(parsed) ? parsed : undefined;
};

export const toDryBulkResource = (resource: ResourceFormData): DryBulkResource => ({
  owner: resource.owner,
  type: resource.type,
  ...(resource.type === "GEARED"
    ? {
        craneCount: numberOrUndefined(resource.craneCount),
        craneCapacityMt: numberOrUndefined(resource.craneCapacityMt),
        craneSwl: numberOrUndefined(resource.craneSwl),
        cranesGoodCondition: resource.cranesGoodCondition ?? undefined,
        grabCount: numberOrUndefined(resource.grabCount),
        grabSizeCbm: numberOrUndefined(resource.grabSizeCbm),
        grabSwl: numberOrUndefined(resource.grabSwl),
        grabsGoodCondition: resource.grabsGoodCondition ?? undefined,
      }
    : { resourceCount: numberOrUndefined(resource.resourceCount) }),
});

/** Form values → save request; the vessel call is set by the BFF, never here (D6). */
export const toDryBulkNominationRequest = (
  values: DryBulkNominationFormData,
  vesselId: string,
  version?: number,
): DryBulkNominationRequest => ({
  vesselId,
  version,
  portCode: values.portCode ? values.portCode : undefined,
  lastPortCode: blankToUndefined(values.lastPortCode),
  nextPortCode: blankToUndefined(values.nextPortCode),
  initialEta: values.initialEta ? convertUaeToUtc(values.initialEta) : undefined,
  maxArrivalDraft: numberOrUndefined(values.maxArrivalDraft),
  remarks: blankToUndefined(values.remarks),
  expectedBerthCode: blankToUndefined(values.expectedBerthCode),
  expectedBerthName: blankToUndefined(values.expectedBerthName),
  purposeOfVisitCode: blankToUndefined(values.purposeOfVisitCode),
  viaNumber: blankToUndefined(values.viaNumber),
  cargoLines: values.cargoLines.map((line) => ({
    operationType: line.operationType,
    cargoCode: line.cargoCode,
    quantityMt: numberOrUndefined(line.quantityMt) as number,
    shipperCompanyCode: blankToUndefined(line.shipperCompanyCode),
    stockPileCode: blankToUndefined(line.stockPileCode),
    receiverCompanyCode: blankToUndefined(line.receiverCompanyCode),
  })),
  activities: values.activities.map((activity) => ({
    activityType: activity.activityType,
    otherText: activity.activityType === "OTHER" ? blankToUndefined(activity.otherText) : undefined,
    dateFrom: activity.dateFrom,
    dateTo: activity.dateTo,
  })),
  resources: values.resources.map(toDryBulkResource),
  attachments: values.attachments.map((attachment) => ({
    documentType: attachment.documentType,
    otherSpecify:
      attachment.documentType === "OTHERS" ? blankToUndefined(attachment.otherSpecify) : undefined,
    documentName: blankToUndefined(attachment.documentName),
    fileUrl: attachment.fileUrl,
    issueDate: attachment.issueDate ? attachment.issueDate.slice(0, 10) : undefined,
    expiryDate: attachment.expiryDate.slice(0, 10),
  })),
});

/** Saved draft → form values (reopening a draft, G-3). */
export const fromDryBulkNominationResponse = (
  response: DryBulkNominationResponse,
): DryBulkNominationFormData => ({
  ...emptyDryBulkNominationForm(),
  portCode: response.portCode ?? "",
  lastPortCode: response.lastPortCode ?? "",
  nextPortCode: response.nextPortCode ?? "",
  initialEta: response.initialEta ? toUaeWallClockDate(response.initialEta) : null,
  maxArrivalDraft: response.maxArrivalDraft ?? undefined,
  remarks: response.remarks ?? "",
  expectedBerthCode: response.expectedBerthCode ?? "",
  expectedBerthName: response.expectedBerthName ?? "",
  purposeOfVisitCode: response.purposeOfVisitCode ?? "",
  viaNumber: response.viaNumber ?? "",
  cargoLines: (response.cargoLines ?? []).map((line) => ({ ...line })),
  activities: (response.activities ?? []).map((activity) => ({
    ...activity,
    otherText: activity.otherText ?? "",
  })),
  resources: (response.resources ?? []).map((resource) => ({ ...resource })),
  attachments: (response.attachments ?? []).map((attachment) => ({
    ...attachment,
    otherSpecify: attachment.otherSpecify ?? "",
    issueDate: attachment.issueDate ?? null,
  })),
});

/** vessel-service field paths ("remarks", "cargoLines[0].stockPileCode") from a 400 Problem Details body. */
export const getFieldErrors = (error: unknown): Record<string, string> => {
  const errors = (error as { response?: { data?: { errors?: unknown } } } | null)?.response?.data
    ?.errors;
  return errors && typeof errors === "object" ? (errors as Record<string, string>) : {};
};

export const getHttpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;
